/**
 * Benchmarks — current capability SOTA per task.
 * Editorial snapshot updated when frontier releases move the leaderboard.
 *
 * Data: /api/frontier/benchmarks
 */
import { useEffect, useMemo, useState } from 'react'
import { Trophy, RefreshCw, ExternalLink, Filter } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Benchmark {
  id: string
  name: string
  domain: string
  metric: string
  sota_model: string
  sota_score: number
  sota_lab: string
  sota_date: string
  human_baseline: number | null
  summary: string
  url: string
}

const DOMAIN_TONE: Record<string, string> = {
  'general-reasoning':  'bg-rose/15 text-rose-light',
  'graduate-science':   'bg-blue/15 text-blue-light',
  'math':               'bg-amber/15 text-amber-light',
  'math-competition':   'bg-amber/15 text-amber-light',
  'research-math':      'bg-amber/15 text-amber-light',
  'frontier-knowledge': 'bg-purple/15 text-purple-light',
  'general-knowledge':  'bg-purple/15 text-purple-light',
  'code':               'bg-emerald/15 text-emerald',
  'agents':             'bg-rose/15 text-rose-light',
  'human-preference':   'bg-cyan/15 text-cyan',
}

export default function BenchmarksPage() {
  const [rows, setRows] = useState<Benchmark[]>([])
  const [domains, setDomains] = useState<string[]>([])
  const [domainFilter, setDomainFilter] = useState('')
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    api.get('/api/frontier/benchmarks')
      .then(r => {
        setRows(r.data?.benchmarks || [])
        setDomains(r.data?.domains || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const filtered = useMemo(() =>
    domainFilter ? rows.filter(b => b.domain === domainFilter) : rows
  , [rows, domainFilter])

  const labLeaderboard = useMemo(() => {
    const tally: Record<string, number> = {}
    for (const b of rows) tally[b.sota_lab] = (tally[b.sota_lab] || 0) + 1
    return Object.entries(tally).sort(([, a], [, b]) => b - a)
  }, [rows])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Trophy size={18} className="text-amber" /> CAPABILITY BENCHMARKS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          current sota per task · who leads where
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Tracked</div>
          <div className="text-2xl font-black font-mono mt-1 text-amber">{rows.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Domains</div>
          <div className="text-2xl font-black font-mono mt-1 text-blue-light">{domains.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">SOTA leader</div>
          <div className="text-sm font-bold text-purple-light mt-1">
            {labLeaderboard[0]?.[0] || '—'}
            <span className="text-text-muted font-mono ml-1 text-xs">
              {labLeaderboard[0]?.[1]} firsts
            </span>
          </div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Filtered</div>
          <div className="text-2xl font-black font-mono mt-1 text-emerald">{filtered.length}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={domainFilter} onChange={e => setDomainFilter(e.target.value)}
          aria-label="Filter by domain">
          <option value="">All domains</option>
          {domains.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Benchmark</th>
              <th className="text-left px-3 py-2">Domain</th>
              <th className="text-left px-3 py-2">SOTA model</th>
              <th className="text-left px-3 py-2">Lab</th>
              <th className="text-right px-3 py-2">Score</th>
              <th className="text-right px-3 py-2">vs Human</th>
              <th className="text-left px-3 py-2">Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(b => (
              <tr key={b.id} className="hover:bg-surface-raised/40">
                <td className="px-3 py-1.5">
                  <div className="font-semibold text-text-primary">{b.name}</div>
                  <div className="text-[10px] text-text-muted">{b.summary}</div>
                </td>
                <td className="px-3 py-1.5">
                  <span className={clsx('badge text-[9px]', DOMAIN_TONE[b.domain] || 'bg-surface-raised text-text-muted')}>
                    {b.domain}
                  </span>
                </td>
                <td className="px-3 py-1.5 font-mono text-emerald">{b.sota_model}</td>
                <td className="px-3 py-1.5 text-purple-light">{b.sota_lab}</td>
                <td className="px-3 py-1.5 font-mono text-right text-amber-light">
                  {b.sota_score}
                  <span className="text-[9px] text-text-muted ml-1">{b.metric}</span>
                </td>
                <td className="px-3 py-1.5 font-mono text-right text-[10px]">
                  {b.human_baseline != null ? (
                    b.sota_score >= b.human_baseline
                      ? <span className="text-emerald">+{(b.sota_score - b.human_baseline).toFixed(1)}</span>
                      : <span className="text-rose">{(b.sota_score - b.human_baseline).toFixed(1)}</span>
                  ) : '—'}
                </td>
                <td className="px-3 py-1.5 font-mono text-text-muted">{b.sota_date}</td>
                <td className="px-3 py-1.5">
                  <a href={b.url} target="_blank" rel="noreferrer" aria-label={`${b.name} reference`}>
                    <ExternalLink size={10} className="text-blue-light" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card p-3">
        <div className="text-[10px] uppercase tracking-widest text-text-muted mb-2">SOTA tally — where each lab leads</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {labLeaderboard.map(([lab, n]) => (
            <div key={lab} className="flex items-center justify-between p-2 rounded bg-surface-raised">
              <span className="text-xs text-text-primary">{lab}</span>
              <span className="font-mono text-sm font-bold text-amber">{n}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
